/**
 * Read-only rendering of a structured feeding plan (migration 0101): one
 * line per slot plus owner-supplied / allergies / red-flag chips. Shared by
 * the staff pet page, booking detail, the Today drawer and the portal.
 */
import {
  type FeedingPlanItem,
  feedingSlotLabel,
  formatFeedingItem,
  sortFeedingItems,
} from '@/lib/feeding';
import { AlertTriangle } from 'lucide-react';

export interface FeedingPlanSummaryProps {
  items: readonly FeedingPlanItem[];
  ownerSupplied?: boolean;
  allergies?: string | null;
  redFlags?: string | null;
  /** Legacy free text shown when there is no structured plan. */
  legacyText?: string | null;
  emptyLabel?: string;
}

export function FeedingPlanSummary({
  items,
  ownerSupplied = false,
  allergies,
  redFlags,
  legacyText,
  emptyLabel = 'No feeding plan on file.',
}: FeedingPlanSummaryProps) {
  const sorted = sortFeedingItems(items);
  if (sorted.length === 0 && !legacyText && !allergies && !redFlags && !ownerSupplied) {
    return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <div className="space-y-2" data-testid="feeding-plan-summary">
      {sorted.length > 0 ? (
        <dl className="divide-y divide-border text-sm">
          {sorted.map((it) => (
            <div key={`${it.slot}:${it.time ?? ''}`} className="flex gap-3 py-1.5">
              <dt className="num w-28 shrink-0 text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground">
                {feedingSlotLabel(it)}
              </dt>
              <dd className="text-foreground">{formatFeedingItem(it)}</dd>
            </div>
          ))}
        </dl>
      ) : legacyText ? (
        <p className="whitespace-pre-line text-sm leading-relaxed text-foreground">{legacyText}</p>
      ) : null}
      {(ownerSupplied || allergies || redFlags) && (
        <div className="flex flex-wrap gap-1.5">
          {ownerSupplied && (
            <span className="ring-hairline rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              Owner-supplied food
            </span>
          )}
          {allergies && (
            <span className="rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning-foreground ring-1 ring-warning/30">
              Allergies: {allergies}
            </span>
          )}
          {redFlags && (
            <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-[11px] font-medium text-destructive ring-1 ring-destructive/25">
              <AlertTriangle className="size-3" strokeWidth={2} />
              {redFlags}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
